import { TFunction } from 'i18next'
import React from 'react'

import { WorkDay } from '../../../types/response'
import {
    DataTableAction,
    DataTableColumn,
    FilterItemConfig,
    FilterToggle,
} from '@/components/ui/data-table-customized'
import {
    getStatusLabel,
    getDayOffTypeLabel,
    getBusinessTypeLabel,
} from '@/modules/WorkDay/constants'
import EditIcons from '@/icons/EditIcons'

const getWorkedDayValue = (workDayTime: number | null): number => {
    if (workDayTime !== null && workDayTime >= 7) return 1
    if (workDayTime !== null && workDayTime > 3) return 0.5
    return 0
}

const getDayOffValue = (row: WorkDay): number | string => {
    if (row.work_day_type === 'Nghỉ phép năm') {
        return row.work_day_option === 2 ? 1 : 0.5
    }
    if (row.work_day_type === 'Nghỉ không phép') return 0
    return ''
}

const getBusinessDayValue = (row: WorkDay): number | string => {
    if (row.work_day_type !== 'Ngày công tác') return ''
    return row.work_day_option === 2 ? 1 : 0.5
}

/**
 * Column definitions for WorkDay list
 */
export const getWorkdayListColumns = (
    t: TFunction
): DataTableColumn<WorkDay>[] => [
    {
        key: 'employee_name',
        title: t('employee-name', { defaultValue: 'Tên nhân viên' }),
        width: 200,
        sortable: true,
        pinned: 'left',
    },
    {
        key: 'department_name',
        title: t('department', { defaultValue: 'Phòng ban' }),
        width: 200,
        sortable: true,
    },
    {
        key: 'work_day_date',
        title: t('work-day-date', { defaultValue: 'Ngày làm việc' }),
        width: 150,
        sortable: true,
        formatter: {
            type: 'date',
            format: 'DD/MM/YYYY',
        },
    },
    {
        key: 'work_day_time',
        title: t('work-time', { defaultValue: 'Giờ làm' }),
        width: 100,
        sortable: true,
        cellClassName: 'text-right text-primary',
        valueGetter: (row) =>
            row.work_day_time ? Number(row.work_day_time).toFixed(2) : '0',
    },
    {
        key: 'work_time_range',
        title: t('work-time-range', { defaultValue: 'Giờ vào - ra' }),
        width: 180,
        formatter: {
            type: 'timerange',
            startKey: 'work_day_in',
            endKey: 'work_day_out',
            format: 'HH:mm',
            separator: ' - ',
            emptyPlaceholder: '-',
        },
    },
    {
        key: 'work_n_day_time',
        title: t('work-day-time', { defaultValue: 'Công làm việc' }),
        width: 250,
        valueGetter: (row) => getWorkedDayValue(row.work_day_time),
    },
    {
        key: 'work_day_day_off',
        title: t('work-day-day-off', { defaultValue: 'Công nghỉ phép' }),
        width: 200,
        valueGetter: getDayOffValue,
    },
    {
        key: 'work_day_business',
        title: t('work-day-business', { defaultValue: 'Công công tác' }),
        width: 150,
        valueGetter: getBusinessDayValue,
    },
    {
        key: 'work_n_day',
        title: t('work-n-day', { defaultValue: 'Hệ số công' }),
        width: 200,
        sortable: true,
    },
    {
        key: 'work_day_type',
        title: t('work-day-type', { defaultValue: 'Loại ngày làm việc' }),
        width: 200,
        sortable: true,
    },
    {
        key: 'related_day_offs',
        title: t('workday.related_day_offs', { defaultValue: 'Nghỉ phép' }),
        width: 180,
        render: (_value, row) => {
            const dayOffs = row.related_day_offs
            if (!dayOffs || dayOffs.length === 0) return '-'
            return (
                <div className="space-y-0.5">
                    {dayOffs.map((d) => (
                        <div
                            key={d.day_off_id}
                            className="flex items-center gap-1 text-xs min-w-0 overflow-hidden"
                        >
                            <span
                                className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${d.day_off_status === 'approved' ? 'bg-green-500' : 'bg-amber-500'}`}
                            />
                            <span className="truncate min-w-0">
                                {getDayOffTypeLabel(d.day_off_type)}
                            </span>
                            <span
                                className={`text-[10px] ml-auto flex-shrink-0 ${d.day_off_status !== 'approved' ? 'text-amber-600' : 'text-green-600'}`}
                            >
                                ({getStatusLabel(d.day_off_status)})
                            </span>
                        </div>
                    ))}
                </div>
            )
        },
    },
    {
        key: 'related_businesses',
        title: t('workday.related_businesses', { defaultValue: 'Công tác' }),
        width: 180,
        render: (_value, row) => {
            const businesses = row.related_businesses
            if (!businesses || businesses.length === 0) return '-'
            return (
                <div className="space-y-0.5">
                    {businesses.map((b) => (
                        <div
                            key={b.business_id}
                            className="flex items-center gap-1 text-xs min-w-0 overflow-hidden"
                        >
                            <span
                                className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${b.business_status === 'approved' ? 'bg-green-500' : 'bg-amber-500'}`}
                            />
                            <span className="truncate min-w-0">
                                {b.business_place ||
                                    getBusinessTypeLabel(b.business_type)}
                            </span>
                            <span
                                className={`text-[10px] ml-auto flex-shrink-0 ${b.business_status !== 'approved' ? 'text-amber-600' : 'text-green-600'}`}
                            >
                                ({getStatusLabel(b.business_status)})
                            </span>
                        </div>
                    ))}
                </div>
            )
        },
    },
]

/**
 * Row actions configuration
 */
export const getWorkdayActions = ({
    t,
    onEdit,
}: {
    t: TFunction
    onEdit: (row: WorkDay) => void
}): DataTableAction<WorkDay>[] => [
    {
        key: 'edit',
        label: t('edit', { defaultValue: 'Chỉnh sửa' }),
        icon: <EditIcons className="w-4 h-4" />,
        onClick: onEdit,
        // Empty days (no attendance record) have no work_day_id -> nothing to edit.
        hidden: (row) => !row.work_day_id,
    },
]

/**
 * Filter items configuration
 */
export const getFilterItems = (
    t: TFunction,
    departmentOptions: Array<{ label: string; value: number }> = []
): FilterItemConfig[] => {
    const currentYear = new Date().getFullYear()
    const years = Array.from(
        { length: currentYear - 2000 + 1 },
        (_, index) => currentYear - index
    )
    const months = Array.from({ length: 12 }, (_, index) => index + 1)

    return [
        {
            type: 'select',
            name: 'employee_status',
            label: t('employee-status', {
                defaultValue: 'Trạng thái nhân viên',
            }),
            options: [
                {
                    label: t('active', { defaultValue: 'Đang làm việc' }),
                    value: 'active',
                },
                {
                    label: t('inactive', { defaultValue: 'Đã nghỉ việc' }),
                    value: 'inactive',
                },
                {
                    label: t('all', { defaultValue: 'Tất cả' }),
                    value: 'all',
                },
            ],
        },
        {
            type: 'multi-select',
            name: 'department_ids',
            label: t('department', { defaultValue: 'Phòng ban' }),
            options: departmentOptions,
        },
        {
            type: 'select',
            name: 'year',
            label: t('year', { defaultValue: 'Năm' }),
            placeholder: t('year-placeholder', { defaultValue: 'Chọn năm' }),
            options: years.map((year) => ({
                label: `${t('year')} ${year}`,
                value: String(year),
            })),
        },
        {
            type: 'select',
            name: 'month',
            label: t('month', { defaultValue: 'Tháng' }),
            placeholder: t('month-placeholder', { defaultValue: 'Chọn tháng' }),
            options: months.map((month) => ({
                label: `${t('month')} ${month}`,
                value: String(month),
            })),
        },
    ]
}

/**
 * Filter toggles for column visibility
 */
export const getFilterToggles = (t: TFunction): FilterToggle[] => [
    {
        key: 'employee_name',
        label: t('employee-name', { defaultValue: 'Tên nhân viên' }),
    },
    {
        key: 'department_name',
        label: t('department', { defaultValue: 'Phòng ban' }),
    },
    {
        key: 'work_day_date',
        label: t('work-day-date', { defaultValue: 'Ngày làm việc' }),
    },
    {
        key: 'work_day_time',
        label: t('work-time', { defaultValue: 'Giờ làm' }),
    },
    {
        key: 'work_time_range',
        label: t('work-time-range', { defaultValue: 'Giờ vào - ra' }),
    },
    {
        key: 'work_n_day_time',
        label: t('work-day-time', { defaultValue: 'Công làm việc' }),
    },
    {
        key: 'work_day_day_off',
        label: t('work-day-day-off', { defaultValue: 'Công nghỉ phép' }),
    },
    {
        key: 'work_day_business',
        label: t('work-day-business', { defaultValue: 'Công công tác' }),
    },
    {
        key: 'work_n_day',
        label: t('work-n-day', { defaultValue: 'Hệ số công' }),
    },
    {
        key: 'work_day_type',
        label: t('work-day-type', { defaultValue: 'Loại ngày làm việc' }),
    },
    {
        key: 'related_day_offs',
        label: t('workday.related_day_offs', { defaultValue: 'Nghỉ phép' }),
    },
    {
        key: 'related_businesses',
        label: t('workday.related_businesses', { defaultValue: 'Công tác' }),
    },
]
